// PNG EXACT — the bake's PNG rasters (class ids, ways field codes, measured
// fields) decoded WITHOUT a 2D canvas (owner ruling 2026-10-25: exact data
// never passes through a canvas — Brave's canvas farbling flips low bits on
// getImageData, which turns a class id into another id). Chunks parsed, every
// chunk's CRC-32 checked, the IDATs inflated by the browser's
// DecompressionStream('deflate') (zlib: Adler-32 checked there), the scanlines
// unfiltered (PNG filters 0–4). 8-bit, non-interlaced: grey, grey + alpha,
// RGB, RGBA, palette (expanded to RGB, RGBA with a tRNS) — what the bake
// writes (PIL). Anything else, a bad CRC, a short stream: a throw, never a
// guess. surface.js runs it in its decode worker (and on the main thread only
// where there is no worker), as it did the canvas decode.
//
// decodePng is SELF-CONTAINED (no module scope): surface.js stringifies it
// into its inline worker. Gate: scripts/check-ring.mjs exact:no-canvas (the
// EXACT-DATA region below) + exact:png (the fixture PNGs byte-exact, twins).

// EXACT-DATA-BEGIN (scripts/check-ring.mjs exact:no-canvas: no canvas, bitmap or readback in here)
// bytes (Uint8Array | ArrayBuffer of a whole .png) -> {w, h, ch, data}: data = w·h·ch
// samples, row 0 = the image's top row; ch 1 grey, 2 grey + alpha, 3 RGB, 4 RGBA
export async function decodePng(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const SIG = [137, 80, 78, 71, 13, 10, 26, 10];
  for (let i = 0; i < 8; i++) if (b[i] !== SIG[i]) throw new Error('png: not a PNG');
  const crcT = new Int32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; crcT[n] = c; }
  const u32 = (o) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
  let w = 0, h = 0, ct = -1, pal = null, trns = null, end = false, idn = 0;
  const idat = [];
  for (let o = 8; o + 12 <= b.length && !end;) {
    const len = u32(o), t = String.fromCharCode(b[o + 4], b[o + 5], b[o + 6], b[o + 7]), d = o + 8;
    if (d + len + 4 > b.length) throw new Error(`png: chunk ${t} runs past the end`);
    let c = -1;
    for (let i = o + 4; i < d + len; i++) c = crcT[(c ^ b[i]) & 255] ^ (c >>> 8);
    if (((c ^ -1) >>> 0) !== u32(d + len)) throw new Error(`png: chunk ${t} CRC mismatch`);
    if (t === 'IHDR') {
      w = u32(d); h = u32(d + 4); ct = b[d + 9];
      if (b[d + 8] !== 8) throw new Error(`png: bit depth ${b[d + 8]} (reads 8)`);
      if (b[d + 12] !== 0) throw new Error('png: interlaced');
      if (![0, 2, 3, 4, 6].includes(ct)) throw new Error(`png: colour type ${ct}`);
    } else if (t === 'PLTE') pal = b.subarray(d, d + len);
    else if (t === 'tRNS') trns = b.subarray(d, d + len);
    else if (t === 'IDAT') { idat.push(b.subarray(d, d + len)); idn += len; }
    else if (t === 'IEND') end = true;
    o = d + len + 4;
  }
  if (!w || !h || !end || !idn) throw new Error(`png: ${!w ? 'no IHDR' : !idn ? 'no IDAT' : 'no IEND'}`);
  if (ct === 3 && !pal) throw new Error('png: palette image without PLTE');
  const z = new Uint8Array(idn);
  for (let i = 0, k = 0; i < idat.length; k += idat[i].length, i++) z.set(idat[i], k);
  const raw = new Uint8Array(await new Response(new Blob([z]).stream()
    .pipeThrough(new DecompressionStream('deflate'))).arrayBuffer());
  const bpp = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 }[ct], st = w * bpp;
  if (raw.length !== h * (st + 1)) throw new Error(`png: ${raw.length} B inflated, want ${h * (st + 1)}`);
  const px = new Uint8Array(h * st);
  const zero = new Uint8Array(st);
  for (let y = 0; y < h; y++) {
    const f = raw[y * (st + 1)], s = y * (st + 1) + 1, o = y * st;
    const r = px.subarray(o, o + st), up = y ? px.subarray(o - st, o) : zero;
    let i = 0;
    if (f === 0) r.set(raw.subarray(s, s + st));
    else if (f === 1) {
      for (; i < bpp; i++) r[i] = raw[s + i];
      for (; i < st; i++) r[i] = raw[s + i] + r[i - bpp];
    } else if (f === 2) for (; i < st; i++) r[i] = raw[s + i] + up[i];
    else if (f === 3) {
      for (; i < bpp; i++) r[i] = raw[s + i] + (up[i] >> 1);
      for (; i < st; i++) r[i] = raw[s + i] + ((r[i - bpp] + up[i]) >> 1);
    } else if (f === 4) {
      for (; i < bpp; i++) r[i] = raw[s + i] + up[i];
      for (; i < st; i++) {
        const a = r[i - bpp], u = up[i], c = up[i - bpp];
        let pa = u - c, pb = a - c, pc = pa + pb;   // |q − a|, |q − u|, |q − c| of q = a + u − c
        if (pa < 0) pa = -pa;
        if (pb < 0) pb = -pb;
        if (pc < 0) pc = -pc;
        r[i] = raw[s + i] + (pa <= pb && pa <= pc ? a : pb <= pc ? u : c);
      }
    } else throw new Error(`png: row ${y} filter ${f}`);
  }
  if (ct !== 3) return { w, h, ch: bpp, data: px };
  const n = w * h, ch = trns ? 4 : 3, out = new Uint8Array(n * ch);
  for (let i = 0; i < n; i++) {
    const k = px[i];
    if (3 * k + 2 >= pal.length) throw new Error(`png: palette index ${k} past the PLTE`);
    out[ch * i] = pal[3 * k]; out[ch * i + 1] = pal[3 * k + 1]; out[ch * i + 2] = pal[3 * k + 2];
    if (ch === 4) out[4 * i + 3] = k < trns.length ? trns[k] : 255;
  }
  return { w, h, ch, data: out };
}
// EXACT-DATA-END

// the PNG signature at the head of the bytes (surface.js routes PNG here, the
// lossy JPEG / WebP colour rasters to its canvas decode)
export const isPng = (b) => b.length >= 8 && b[0] === 137 && b[1] === 80 && b[2] === 78 && b[3] === 71;
